import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SUPABASE_URL, supabaseHeaders } from '../supabase';
import { uploadPoster } from '../utils/imageUpload';
import { tagAtPatchOnCreate } from '../utils/tagTimestamps';

function SeriesForm({ onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state || {};
  const [todoItemId] = useState(prefill.todoItemId || null);
  const [prefillPosterUrl] = useState(prefill.posterUrl || null);
  const [form, setForm] = useState({
    title: prefill.title || '',
    season: prefill.season || '',
    rating: '',
    thoughts: '',
    marvel: false,
    dc: false,
    watchedWithJoy: false,
    watchedWithRick: false,
    watchedWithLisa: false,
    watchedWithGionnan: false,
  });
  const [poster, setPoster] = useState(null);
  const [posterPreview, setPosterPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.rating) {
      setStatus('Please fill in a title and rating.');
      return;
    }

    setLoading(true);
    setStatus('');

    try {
      let posterUrl = null;
      if (poster) posterUrl = await uploadPoster(poster);
      else if (prefillPosterUrl) posterUrl = prefillPosterUrl;

      const payload = {
        title: form.title,
        rating: parseFloat(form.rating),
        marvel: form.marvel,
        dc: form.dc,
        watched_with_joy: form.watchedWithJoy,
        watched_with_rick: form.watchedWithRick,
        watched_with_lisa: form.watchedWithLisa,
        watched_with_gionnan: form.watchedWithGionnan,
        thoughts: form.thoughts || '',
        ...tagAtPatchOnCreate([
          { isOn: form.watchedWithJoy, atField: 'watched_with_joy_at' },
          { isOn: form.watchedWithRick, atField: 'watched_with_rick_at' },
          { isOn: form.watchedWithLisa, atField: 'watched_with_lisa_at' },
          { isOn: form.watchedWithGionnan, atField: 'watched_with_gionnan_at' },
        ]),
      };

      if (form.season) payload.season = form.season;
      if (posterUrl) payload.poster_url = posterUrl;

      const res = await fetch(`${SUPABASE_URL}/rest/v1/series`, {
        method: 'POST',
        headers: { ...supabaseHeaders(true), Prefer: 'return=representation' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to create');

      if (todoItemId) {
        await fetch(`${SUPABASE_URL}/rest/v1/todo_series?id=eq.${todoItemId}`, {
          method: 'DELETE',
          headers: supabaseHeaders(true),
        });
      }

      setStatus('Saved :D');
      if (onClose) {
        setTimeout(() => onClose(), 1500);
      } else {
        setTimeout(() => navigate('/Series'), 1500);
      }
    } catch (err) {
      console.error(err);
      setStatus(`Something went wrong: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (onClose) {
    return (
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <h2 style={{ fontFamily: 'angelWish', fontSize: '2rem', letterSpacing: '0.06em', marginBottom: 0 }}>Add Series</h2>
        <div className="form-group">
          <label>Title *</label>
          <input type="text" name="title" value={form.title} onChange={handleChange} required />
        </div>

        <div className="form-group">
          <label>Season</label>
          <input type="text" name="season" value={form.season} onChange={handleChange} />
        </div>

        <div className="form-group">
          <label>Rating *</label>
          <input
            type="number"
            name="rating"
            value={form.rating}
            onChange={handleChange}
            min="1"
            max="10"
            step="0.1"
            required
          />
        </div>

        <div className="form-group">
          <label>Thoughts</label>
          <textarea name="thoughts" value={form.thoughts} onChange={handleChange} rows={4} />
        </div>

        <div className="form-group">
          <label>Poster</label>
          {(posterPreview || (prefillPosterUrl && !poster)) && (
            <img src={posterPreview || prefillPosterUrl} alt="poster" style={{ width: 80, borderRadius: 4, marginBottom: '0.4rem' }} />
          )}
          <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files[0]; setPoster(f); setPosterPreview(f ? URL.createObjectURL(f) : null); }} />
        </div>

        <div className="form-group">
          <label>Marvel/DC</label>
          <div className="form-checkboxes">
            <label><input type="checkbox" name="marvel" checked={form.marvel} onChange={handleChange} /> Marvel</label>
            <label><input type="checkbox" name="dc" checked={form.dc} onChange={handleChange} /> DC</label>
          </div>
        </div>

        <div className="form-group">
          <label>With</label>
          <div className="form-checkboxes">
            <label><input type="checkbox" name="watchedWithJoy" checked={form.watchedWithJoy} onChange={handleChange} /> Joy &lt;3</label>
            <label><input type="checkbox" name="watchedWithRick" checked={form.watchedWithRick} onChange={handleChange} /> Rick</label>
            <label><input type="checkbox" name="watchedWithLisa" checked={form.watchedWithLisa} onChange={handleChange} /> Lisa</label>
            <label><input type="checkbox" name="watchedWithGionnan" checked={form.watchedWithGionnan} onChange={handleChange} /> Gionnan</label>
          </div>
        </div>

        {status && <p className="form-status">{status}</p>}
        <div className="edit-modal-actions">
          <button type="button" className="form-submit" onClick={onClose} style={{ background: 'white', color: 'black', WebkitTextFillColor: 'black' }}>Cancel</button>
          <button type="submit" disabled={loading} className="form-submit">
            {loading ? 'Loading...' : 'Add Series'}
          </button>
        </div>
      </form>
    );
  }

  return (
    <div className="app-container">
      <form onSubmit={handleSubmit} className="movie-form">
        <h1 style={{ fontFamily: 'angelWish', fontSize: '4rem', letterSpacing: '0.06em', marginTop: 0, marginBottom: 0 }}>Add Series</h1>
        <div className="form-group">
          <label>Title *</label>
          <input type="text" name="title" value={form.title} onChange={handleChange} required />
        </div>

        <div className="form-group">
          <label>Season</label>
          <input type="text" name="season" value={form.season} onChange={handleChange} />
        </div>

        <div className="form-group">
          <label>Rating *</label>
          <input
            type="number"
            name="rating"
            value={form.rating}
            onChange={handleChange}
            min="1"
            max="10"
            step="0.1"
            required
          />
        </div>

        <div className="form-group">
          <label>Thoughts</label>
          <textarea name="thoughts" value={form.thoughts} onChange={handleChange} rows={4} />
        </div>

        <div className="form-group">
          <label>Poster</label>
          {(posterPreview || (prefillPosterUrl && !poster)) && (
            <img src={posterPreview || prefillPosterUrl} alt="poster" style={{ width: 80, borderRadius: 4, marginBottom: '0.4rem' }} />
          )}
          <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files[0]; setPoster(f); setPosterPreview(f ? URL.createObjectURL(f) : null); }} />
        </div>

        <div className="form-group">
          <label>Marvel/DC</label>
          <div className="form-checkboxes">
            <label><input type="checkbox" name="marvel" checked={form.marvel} onChange={handleChange} /> Marvel</label>
            <label><input type="checkbox" name="dc" checked={form.dc} onChange={handleChange} /> DC</label>
          </div>
        </div>

        <div className="form-group">
          <label>With</label>
          <div className="form-checkboxes">
            <label><input type="checkbox" name="watchedWithJoy" checked={form.watchedWithJoy} onChange={handleChange} /> Joy &lt;3</label>
            <label><input type="checkbox" name="watchedWithRick" checked={form.watchedWithRick} onChange={handleChange} /> Rick</label>
            <label><input type="checkbox" name="watchedWithLisa" checked={form.watchedWithLisa} onChange={handleChange} /> Lisa</label>
            <label><input type="checkbox" name="watchedWithGionnan" checked={form.watchedWithGionnan} onChange={handleChange} /> Gionnan</label>
          </div>
        </div>

        <button type="submit" disabled={loading} className="form-submit">
          {loading ? 'Loading...' : 'Add Series'}
        </button>

        {status && <p className="form-status">{status}</p>}
      </form>
    </div>
  );
}

export default SeriesForm;
